/**
 * Portada del portal público: **qué se juega hoy**.
 *
 * Es la pantalla que la federación deja proyectada un sábado y la que abre el
 * periodista desde el teléfono, así que el orden de lectura está decidido y no
 * es alfabético: primero lo que se está jugando ahora, después el resto del
 * día, después lo que viene, y al final los últimos resultados.
 *
 * El corte de "hoy" lo hace ESTA pantalla con el reloj del browser
 * (`matchesOfDay`), no el server. La razón está en ADR-020: el server no sabe
 * el huso horario del que mira —un partido de las 21:00 en Salta es del día
 * siguiente en UTC— y, si lo supiera, habría una entrada de caché por huso y el
 * snapshot dejaría de servir para todos.
 *
 * El componente está partido en dos —el que pollea y la VISTA pura— para que la
 * vista se pueda renderizar con datos a mano y sin red
 * (`test/public-pages.test.tsx`). Mismo criterio en las otras tres pantallas.
 */
import { useT } from '../../i18n/use-t';
import {
  matchesOfDay,
  type PublicClient,
  type PublicFixtureEntry,
  type PublicHomeResponse,
} from '../../lib/public-client';
import { publicCompetitionPath, publicHref, publicResultsPath } from '../../lib/public-router';
import { usePolled } from './hooks';
import { EmptyBlock, MatchCard, Section, interceptable } from './parts';
import { PublicState, Updated } from './PublicState';

export function PublicHome({
  client,
  slug,
  base,
  onNavigate,
}: {
  client: PublicClient;
  slug: string;
  base: string;
  onNavigate: (href: string) => void;
}): JSX.Element {
  const t = useT();
  const state = usePolled<PublicHomeResponse>(() => client.getHome(slug), [slug]);

  if (state.data === null) return <PublicState state={state} what={t('publicPortal.today')} />;

  return (
    <>
      <PublicHomeView
        home={state.data}
        slug={slug}
        base={base}
        onNavigate={onNavigate}
        stale={state.error !== null}
        now={new Date()}
      />
      <Updated updatedAt={state.updatedAt} error={state.error} />
    </>
  );
}

export function PublicHomeView({
  home,
  slug,
  base,
  onNavigate,
  stale = false,
  now,
}: {
  home: PublicHomeResponse;
  slug: string;
  base: string;
  onNavigate: (href: string) => void;
  stale?: boolean;
  now: Date;
}): JSX.Element {
  const t = useT();

  const hoy = matchesOfDay(home, now);
  // Lo que viene y NO es de hoy: si no se saca, el partido de esta tarde
  // aparecería dos veces en la misma pantalla.
  const deHoy = new Set(hoy.map((e) => e.id));
  const proximos = home.upcoming.filter((e) => !deHoy.has(e.id));
  const recientes = home.recent.filter((e) => !deHoy.has(e.id)).slice(0, 6);

  const card = (entry: PublicFixtureEntry): JSX.Element => (
    <MatchCard key={entry.id} slug={slug} base={base} entry={entry} onNavigate={onNavigate} />
  );

  return (
    <>
      <h1 className="pub-title">{t('publicPortal.today')}</h1>
      <p className="pub-sub">{t('publicPortal.todayHint')}</p>

      {stale ? <p className="pub-warn">{t('publicPortal.stale')}</p> : null}

      <Section title={t('publicPortal.todaySection')}>
        {hoy.length === 0 ? (
          <EmptyBlock>{t('publicPortal.noMatchesToday')}</EmptyBlock>
        ) : (
          <div className="pub-grid">{hoy.map(card)}</div>
        )}
      </Section>

      {proximos.length > 0 ? (
        <Section title={t('publicPortal.upcoming')}>
          <div className="pub-grid">{proximos.map(card)}</div>
        </Section>
      ) : null}

      {recientes.length > 0 ? (
        <Section
          title={t('publicPortal.recent')}
          action={
            <a
              className="pub-chip"
              href={publicHref(publicResultsPath(slug), base)}
              onClick={interceptable(publicHref(publicResultsPath(slug), base), onNavigate)}
            >
              {t('publicPortal.seeAll')}
            </a>
          }
        >
          <div className="pub-grid">{recientes.map(card)}</div>
        </Section>
      ) : null}

      <Section title={t('publicPortal.competitions')}>
        {home.competitions.length === 0 ? (
          <EmptyBlock>{t('publicPortal.noCompetitions')}</EmptyBlock>
        ) : (
          <div className="pub-grid">
            {home.competitions.map((competition) => {
              const href = publicHref(publicCompetitionPath(slug, competition.id), base);
              return (
                <a
                  key={competition.id}
                  className="pub-match"
                  href={href}
                  onClick={interceptable(href, onNavigate)}
                >
                  <div className="pub-match-top">
                    <span>{competition.season}</span>
                    <span className="sep">·</span>
                    <span>{t('publicPortal.teamsCount', { count: competition.teams })}</span>
                    <span className="sep">·</span>
                    <span>{t('publicPortal.matchesCount', { count: competition.matches })}</span>
                  </div>
                  <div className="pub-team">
                    <span className="pub-team-name">{competition.name}</span>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </Section>
    </>
  );
}
